import React, { FC, ReactNode, Suspense, useMemo, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { useAuth } from '@thingslog/utils';
import Navbar from './Navbar';
import { useCurrentCompanyId, useViewport } from '../../hooks';
import { cn } from '@thingslog/ui-components';
import { PageFooter } from '../page-footer';
import SettingsDto, { SolutionSettings } from '@thingslog/repositories/src/settings/SettingsDto';
import { settingsQueryClient } from '../../utils/ReactQueryClients';

interface MainProps {
  children: ReactNode;
}

const Main: FC<MainProps> = ({ children }: MainProps): JSX.Element => {
  return <main className="relative mx-auto flex min-h-full flex-col">{children}</main>;
};

const NavbarLayoutRoute: FC<NavbarLayoutRouteProps> = () => {
  const { isAuthenticated } = useAuth();
  const isMobile = useViewport('sm');
  const companyId = useCurrentCompanyId();
  const [settings, setSettings] = useState<SolutionSettings | null>(null);
  const { useSettings } = useMemo(() => settingsQueryClient, []);

  useSettings(companyId ?? null, {
    refetchOnWindowFocus: false,
    enabled: isAuthenticated,
    onSuccess: (settings: SettingsDto) => {
      setSettings(settings.data);
    },
  });

  if (!isAuthenticated) {
    return (
      <Suspense fallback={<div>Loading...</div>}>
        <Outlet />
      </Suspense>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div
        className={cn('flex h-[100dvh] min-w-0 overflow-hidden', 'flex-col-reverse sm:flex-row')}
      >
        <Navbar variant={isMobile ? 'horizontal' : 'vertical'} />
        <main className="flex min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto">
            <Outlet />
          </div>
          <PageFooter
            className="shrink-0"
            mainLabel={settings?.portalFooterMainLabel || undefined}
            link={
              settings?.portalFooterLinkUrl
                ? {
                    label: settings.portalFooterLinkLabel || settings.portalFooterLinkUrl,
                    url: settings.portalFooterLinkUrl,
                  }
                : undefined
            }
          />
        </main>
      </div>
    </Suspense>
  );
};

interface NavbarLayoutRouteProps {}

export default NavbarLayoutRoute;
